import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, Plane } from 'lucide-react';

import { type AccountEntity } from 'loot-core/types/models';
import { send } from 'loot-core/src/platform/client/fetch';

import { useTravelAccounts } from '../../hooks/useTravelAccounts';
import { Checkbox } from '../ui/checkbox';
import { Budget } from '../budget';

function AccountCombobox({
  accounts,
  selectedIds,
  onChange,
}: {
  accounts: AccountEntity[];
  selectedIds: Set<string>;
  onChange: (ids: Set<string>) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const filtered = useMemo(
    () => accounts.filter(a => a.name.toLowerCase().includes(search.toLowerCase())),
    [accounts, search],
  );
  const allSelected = selectedIds.size === accounts.length;
  const noneSelected = selectedIds.size === 0;

  function toggleAll() {
    onChange(allSelected ? new Set() : new Set(accounts.map(a => a.id)));
  }
  function toggle(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  }

  const label = useMemo(() => {
    if (noneSelected || allSelected) return t('All accounts');
    if (selectedIds.size === 1) return accounts.find(a => selectedIds.has(a.id))?.name ?? '';
    return t('{{count}} accounts', { count: selectedIds.size });
  }, [accounts, selectedIds, allSelected, noneSelected, t]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex min-w-[200px] items-center justify-between gap-2 rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] leading-none hover:bg-accent hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 cursor-pointer"
      >
        <span className="truncate">{label}</span>
        <ChevronsUpDown className="ml-2 size-3.5 shrink-0 opacity-50" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-[240px] rounded-md border bg-popover text-popover-foreground shadow-md">
          <div className="border-b px-3 py-2">
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('Search accounts…')}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            <button
              type="button"
              onClick={toggleAll}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
            >
              <Checkbox checked={allSelected ? true : noneSelected ? false : 'indeterminate'} onCheckedChange={toggleAll} className="pointer-events-none" />
              <span className="font-medium">{t('All accounts')}</span>
            </button>
            <div className="my-1 border-t" />
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-muted-foreground">{t('No accounts found.')}</p>
            )}
            {filtered.map(account => {
              const checked = selectedIds.has(account.id);
              return (
                <button
                  type="button"
                  key={account.id}
                  onClick={() => toggle(account.id)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggle(account.id)} className="pointer-events-none" />
                  <span className="truncate">{account.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function TravelSummary() {
  const { t } = useTranslation();
  const travelAccounts = useTravelAccounts();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(travelAccounts.map(a => a.id)),
  );

  const [startMonth, setStartMonth] = useState<string | undefined>();
  const [travelBounds, setTravelBounds] = useState<{ start: string; end: string } | undefined>();
  const [sparseMonths, setSparseMonths] = useState<string[] | undefined>();
  const [monthToAccounts, setMonthToAccounts] = useState<Record<string, { id: string; name: string; color: string }[]>>({});

  // Deterministic hue from account ID string
  function idToHue(id: string): number {
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    return hash % 360;
  }

  // Sync selectedIds when travelAccounts changes (e.g. account added/removed)
  const prevAccountIdsRef = useRef<string[]>([]);
  useEffect(() => {
    const ids = travelAccounts.map(a => a.id);
    if (ids.join(',') !== prevAccountIdsRef.current.join(',')) {
      prevAccountIdsRef.current = ids;
      setSelectedIds(new Set(ids));
    }
  }, [travelAccounts]);

  // Apply account filter + compute dynamic timeframe
  useEffect(() => {
    const ids = [...selectedIds];
    if (ids.length === 0) {
      send('budget-set-account-filter', { accountIds: null });
      setStartMonth(undefined);
      return;
    }

    send('budget-set-account-filter', { accountIds: ids });
    send('get-months-with-data-for-accounts', { accountIds: ids }).then((months: string[]) => {
      setSparseMonths(months.length > 0 ? months : undefined);
      if (months.length > 0) {
        const bounds = { start: months[0], end: months[months.length - 1] };
        setTravelBounds(bounds);
      }
    });
    send('get-account-months-map', { accountIds: ids }).then(
      (rows: { month: string; accountId: string }[]) => {
        const map: Record<string, { id: string; name: string; color: string }[]> = {};
        for (const { month, accountId } of rows) {
          const account = travelAccounts.find(a => a.id === accountId);
          if (account) {
            if (!map[month]) map[month] = [];
            const hue = idToHue(account.id);
            map[month].push({ id: account.id, name: account.name, color: `hsl(${hue} 60% 42%)` });
          }
        }
        setMonthToAccounts(map);
      },
    );
  }, [selectedIds]);

  // Clear filter on unmount so /budget returns to global view
  useEffect(() => {
    return () => {
      send('budget-set-account-filter', { accountIds: null });
    };
  }, []);

  if (travelAccounts.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="max-w-sm text-center text-sm text-muted-foreground">
          {t('No travel accounts yet. Create an account and choose "Travel" as the account type.')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header + toolbar combined */}
      <div className="flex items-center justify-between border-b bg-blue-50/60 dark:bg-blue-950/20 px-4 py-2">
        <div className="flex items-center gap-2.5">
          <Plane className="size-4 text-blue-500 dark:text-blue-400" />
          <span className="text-sm font-semibold tracking-wide text-blue-700 dark:text-blue-300">
            {t('Travel Budget')}
          </span>
        </div>
        <AccountCombobox
          accounts={travelAccounts}
          selectedIds={selectedIds}
          onChange={setSelectedIds}
        />
      </div>
      <Budget startMonth={startMonth} monthBounds={travelBounds} sparseMonths={sparseMonths} hideIncome monthToAccounts={monthToAccounts} />
    </div>
  );
}
